import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';
import DataTable from 'react-data-table-component';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { toast } from 'react-toastify';
import Breadcrumbs from '../CommonElements/Breadcrumbs';
import { createPassLink, getPassLinks } from '../ReduxToolkit/Reducers/organizationManagement';

const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;

// Opened 2+ days ago without a trial: the admin follows up (links aren't emailed, so there's no reminder)
const linkStatus = (row) => {
  if (row.completed_at) return ['light-success', 'Trial started'];
  if (row.expires_at && new Date(row.expires_at) < new Date()) return ['light-secondary', 'Expired'];
  if (row.opened_at && Date.now() - new Date(row.opened_at) > TWO_DAYS_MS) {
    return ['light-warning', 'Opened 2+ days ago'];
  }
  if (row.opened_at) return ['light-info', 'Opened'];
  return ['light-dark', 'Not opened'];
};

export const copyPassLink = (url) =>
  navigator.clipboard.writeText(url).then(() => toast.success('Link copied', { autoClose: 2000 }));

/* Seasonal Pass payment links (Seasonal-Pass.md Phase 2): the customer opens the link on the app, saves a
   card and starts the 7-day trial. Links are valid 14 days and are sent by the admin, not by email from us. */
function SeasonalPassLinks() {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchText, setSearchText] = useState('');
  const [links, setLinks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [existingPlanAction, setExistingPlanAction] = useState(null);
  // Off for renewals: the card is charged as soon as it's saved
  const [allowTrial, setAllowTrial] = useState(true);
  const [createdUrl, setCreatedUrl] = useState('');

  const fetchLinks = async () => {
    setLoading(true);
    const result = await dispatch(getPassLinks(page, searchText, itemsPerPage));
    setLinks(result?.data?.data || []);
    setTotal(result?.data?.count || 0);
    setLoading(false);
  };

  useEffect(() => {
    fetchLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, itemsPerPage, searchText]);

  const searchLinks = (inputValue) => {
    setPage(1);
    setSearchText(inputValue?.trim());
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(debounce(searchLinks, 700), []);

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setEmail('');
    setExistingPlanAction(null);
    setAllowTrial(true);
    setCreatedUrl('');
  };

  const onCreateLink = async () => {
    if (!email.trim()) return;
    const result = await dispatch(
      createPassLink({
        email: email.trim(),
        existing_plan_action: existingPlanAction || undefined,
        allow_trial: allowTrial,
      }),
    );
    if (result?.statusCode === 200) {
      setCreatedUrl(result.data.url);
      fetchLinks();
    }
  };

  const columns = [
    { name: 'Email', selector: (row) => row.email, minWidth: '220px' },
    {
      name: 'Organization',
      selector: (row) => row.organization?.name || 'New account',
      minWidth: '180px',
    },
    {
      name: 'Created',
      cell: (row) =>
        `${new Date(row.created_at).toLocaleDateString()}${row.created_by_name ? ` by ${row.created_by_name}` : ''}`,
      minWidth: '180px',
    },
    {
      name: 'Charge',
      selector: (row) => (row.allow_trial ? 'After 7-day trial' : 'Immediately'),
      minWidth: '150px',
    },
    {
      name: 'Status',
      cell: (row) => {
        const [color, label] = linkStatus(row);
        return <Badge color={color} style={{ fontSize: '12px' }}>{label}</Badge>;
      },
      minWidth: '180px',
    },
    {
      name: 'Link',
      cell: (row) => (
        <Button size="sm" color="primary" outline onClick={() => copyPassLink(row.url)}>
          Copy link
        </Button>
      ),
      minWidth: '120px',
    },
  ];

  return (
    <div className="page-body">
      <Breadcrumbs pageTitle="Seasonal Pass Links" parent="Home" title="Seasonal Pass Links" />
      <Container fluid>
        <Card>
          <CardBody>
            <Row className="align-items-center justify-content-between mb-3">
              <Col xs="auto">
                <Button color="primary" onClick={() => setCreateModalOpen(true)}>
                  Create Payment Link
                </Button>
              </Col>
              <Col xs="auto" className="d-flex align-items-center gap-2">
                <Label className="mb-0">Search:</Label>
                <Input
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    debounceSearch(e.target.value);
                  }}
                  placeholder="Search by email"
                  style={{ minWidth: 320 }}
                />
              </Col>
            </Row>

            <DataTable
              striped
              pagination
              paginationServer
              data={links}
              columns={columns}
              progressPending={loading}
              paginationTotalRows={total}
              paginationPerPage={itemsPerPage}
              paginationDefaultPage={page}
              customStyles={{ tableWrapper: { style: { minHeight: 500 } } }}
              onChangePage={(newPage) => setPage(newPage)}
              onChangeRowsPerPage={(count) => {
                setPage(1);
                setItemsPerPage(count);
              }}
            />
          </CardBody>
        </Card>
      </Container>

      <Modal isOpen={createModalOpen} toggle={closeCreateModal}>
        <ModalHeader toggle={closeCreateModal}>Create Seasonal Pass Payment Link</ModalHeader>
        <ModalBody>
          {createdUrl ? (
            <>
              <Alert color="success">
                Link created. It&apos;s valid for 14 days; send it to the customer.
              </Alert>
              <Input value={createdUrl} readOnly className="mb-2" />
              <Button color="primary" onClick={() => copyPassLink(createdUrl)}>
                Copy link
              </Button>
            </>
          ) : (
            <>
              <FormGroup>
                <Label>Customer email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="owner@brand.com"
                />
              </FormGroup>
              <div className="form-check mb-2">
                <Input
                  type="checkbox"
                  id="pass-link-trial"
                  checked={allowTrial}
                  onChange={(e) => setAllowTrial(e.target.checked)}
                  className="form-check-input"
                />
                <Label className="form-check-label" htmlFor="pass-link-trial">
                  Start with a 7-day trial
                </Label>
              </div>
              <div className="text-muted mb-3" style={{ fontSize: '13px' }}>
                With no account yet, the customer registers from the link.{' '}
                {allowTrial
                  ? 'They save a card, get a 7-day trial, and are charged €750 + VAT when it ends, unless they cancel during the trial.'
                  : 'They save a card and are charged €750 + VAT at once; the pass starts right away. Use this for renewals.'}
              </div>
              <Label style={{ fontWeight: '600' }}>If this account already has an active plan</Label>
              {[
                ['transfer', 'Cancel it and transfer the remaining credits'],
                ['fresh', 'Cancel it and start fresh'],
              ].map(([key, label]) => (
                <div className="form-check" key={key}>
                  <Input
                    type="radio"
                    id={`pass-link-${key}`}
                    name="passLinkExistingPlan"
                    checked={existingPlanAction === key}
                    onChange={() => setExistingPlanAction(key)}
                    className="form-check-input"
                  />
                  <Label className="form-check-label" htmlFor={`pass-link-${key}`}>
                    {label}
                  </Label>
                </div>
              ))}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeCreateModal}>
            {createdUrl ? 'Done' : 'Cancel'}
          </Button>
          {!createdUrl && (
            <Button color="primary" onClick={onCreateLink} disabled={!email.trim()}>
              Create Link
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default SeasonalPassLinks;
